import { addDays, addMonths, format } from "date-fns";
import { AlarmClock, AlarmClockOff } from "lucide-react";
import { useState } from "react";
import {
  useNotify,

  useRefresh,
  useTranslate,
  useUpdate,
} from "ra-core";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";

import type { Deal } from "../types";

/**
 * Returns true when the deal has an active snooze (date is in the future).
 * A past `snoozed_until` is treated as expired and the card returns to the board.
 */
export const isDealSnoozed = (deal: Pick<Deal, "snoozed_until">): boolean => {
  if (!deal.snoozed_until) return false;
  return new Date(deal.snoozed_until).getTime() > Date.now();
};

type SnoozePopoverProps = {
  deal: Deal;
  trigger: React.ReactNode;
  onOpenChange?: (open: boolean) => void;
};

/** Popover with quick presets + custom date input that sets `deals.snoozed_until`. */
export const SnoozePopover = ({
  deal,
  trigger,
  onOpenChange,
}: SnoozePopoverProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [update, { isPending }] = useUpdate();

  const applySnooze = (until: Date) => {
    // Use end of day local time so a "1 day" snooze lasts the rest of today + tomorrow,
    // rather than expiring at the same minute the user clicked.
    const at = new Date(
      until.getFullYear(),
      until.getMonth(),
      until.getDate(),
      23,
      59,
      59,
      999,
    );
    update(
      "deals",
      {
        id: deal.id,
        data: { snoozed_until: at.toISOString() },
        previousData: deal,
      },
      {
        onSuccess: () => {
          setOpen(false);
          onOpenChange?.(false);
          notify("resources.deals.snoozed.success", {
            type: "info",
            undoable: false,
          });
          refresh();
        },
        onError: () => {
          notify("resources.deals.snoozed.error", { type: "error" });
        },
      },
    );
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };

  const today = new Date();
  const currentValue = deal.snoozed_until
    ? format(new Date(deal.snoozed_until), "yyyy-MM-dd")
    : "";

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        className="w-72 p-3"
        align="end"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="space-y-3">
          <div className="text-sm font-medium">
            {translate("resources.deals.snoozed.popover_title")}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={isPending}
              onClick={(e) => {
                e.stopPropagation();
                applySnooze(addDays(today, 1));
              }}
            >
              {translate("resources.deals.snoozed.preset_tomorrow")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={isPending}
              onClick={(e) => {
                e.stopPropagation();
                applySnooze(addDays(today, 3));
              }}
            >
              {translate("resources.deals.snoozed.preset_three_days")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={isPending}
              onClick={(e) => {
                e.stopPropagation();
                applySnooze(addDays(today, 7));
              }}
            >
              {translate("resources.deals.snoozed.preset_week")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={isPending}
              onClick={(e) => {
                e.stopPropagation();
                applySnooze(addMonths(today, 1));
              }}
            >
              {translate("resources.deals.snoozed.preset_month")}
            </Button>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">
              {translate("resources.deals.snoozed.custom_label")}
            </label>
            <Input
              type="date"
              defaultValue={currentValue}
              min={format(addDays(today, 1), "yyyy-MM-dd")}
              disabled={isPending}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                if (!e.target.value) return;
                applySnooze(new Date(e.target.value));
              }}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

/** Compact icon-only trigger meant for the Kanban card. */
export const SnoozeIconButton = ({ deal }: { deal: Deal }) => {
  const translate = useTranslate();
  return (
    <SnoozePopover
      deal={deal}
      trigger={
        <Button
          size="icon"
          variant="ghost"
          className="h-6 w-6 text-muted-foreground/70 hover:text-foreground hover:bg-muted/60 opacity-70 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100 transition-opacity"
          aria-label={translate("resources.deals.snoozed.action")}
          title={translate("resources.deals.snoozed.action")}
          onClick={(e) => {
            // Don't navigate to the deal — only open the popover.
            e.stopPropagation();
          }}
        >
          <AlarmClock className="h-3.5 w-3.5" />
        </Button>
      }
    />
  );
};

/** Full-width button with label, used in DealShow. */
export const SnoozeButton = ({ deal }: { deal: Deal }) => {
  const translate = useTranslate();
  return (
    <SnoozePopover
      deal={deal}
      trigger={
        <Button
          size="sm"
          variant="outline"
          className="flex items-center gap-2 h-9"
        >
          <AlarmClock className="w-4 h-4" />
          {translate("resources.deals.snoozed.action")}
        </Button>
      }
    />
  );
};

/** Resets `snoozed_until` back to NULL — returns the card to the active board. */
export const UnsnoozeButton = ({ deal }: { deal: Deal }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [update, { isPending }] = useUpdate();

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    update(
      "deals",
      {
        id: deal.id,
        data: { snoozed_until: null },
        previousData: deal,
      },
      {
        onSuccess: () => {
          notify("resources.deals.unsnoozed.success", {
            type: "info",
            undoable: false,
          });
          refresh();
        },
        onError: () => {
          notify("resources.deals.unsnoozed.error", { type: "error" });
        },
      },
    );
  };

  return (
    <Button
      onClick={handleClick}
      size="sm"
      variant="outline"
      disabled={isPending}
      className="flex items-center gap-2 h-9"
    >
      <AlarmClockOff className="w-4 h-4" />
      {translate("resources.deals.unsnoozed.action")}
    </Button>
  );
};
